<app-step-dialog-shell
  [controller]="this"
  mode="tabs"
  [title]="title"
  subtitle="Connect your payroll system or bank account."
  [showClose]="true"
  primaryLabel="Save Connection"
  [disablePrimary]="isSaving || isBankElementBusy || isDiscoveringPaycorLegalEntities || !hasActiveCompany"
  (closeClicked)="close()"
  (cancelClicked)="close()"
  (primaryClicked)="onSave()"
>
  <ng-template [stepDialogStep]="'payroll'">
    <div class="tab-card" [formGroup]="payrollForm">
      <div class="field-row" *ngIf="shouldShowProviderSelector">
        <label>Payroll Provider</label>
        <mat-form-field appearance="outline" class="full-width">
          <mat-select formControlName="providerSlug">
            <mat-option *ngFor="let provider of supportedPayrollSetupProviders" [value]="provider.value">
              {{ provider.label }}
            </mat-option>
          </mat-select>
          <mat-error *ngIf="payrollForm.get('providerSlug')?.hasError('required')">
            Payroll provider is required
          </mat-error>
        </mat-form-field>
      </div>

      <div class="field-row">
        <label>Name</label>
        <mat-form-field appearance="outline" class="full-width">
          <input matInput placeholder="Company name" formControlName="name" readonly />
        </mat-form-field>
      </div>

      <ng-container *ngIf="isUkgReadyProvider">
        <div class="field-row">
          <label>Company Short Name</label>
          <mat-form-field appearance="outline" class="full-width">
            <input matInput placeholder="Enter company short name" formControlName="companyShortName" />
            <mat-error *ngIf="payrollForm.get('companyShortName')?.hasError('required')">
              Company short name is required
            </mat-error>
          </mat-form-field>
          <p class="hint">
            Copy this from your UKG Ready system. If you don't know where to find it,
            <a href="https://campaign.wallit.app/ukg-wallit-connection-steps" target="_blank" rel="noopener noreferrer">see the instructions here</a>.
          </p>
        </div>

        <div class="field-row">
          <label>Client ID</label>
          <mat-form-field appearance="outline" class="full-width">
            <input matInput placeholder="Enter client ID" formControlName="clientId" />
            <mat-error *ngIf="payrollForm.get('clientId')?.hasError('required')">
              Client ID is required
            </mat-error>
          </mat-form-field>
          <p class="hint">
            Copy this from your UKG Ready system. If you don't know where to find it,
            <a href="https://campaign.wallit.app/ukg-wallit-connection-steps" target="_blank" rel="noopener noreferrer">see the instructions here</a>.
          </p>
        </div>

        <div class="field-row">
          <label>Client Secret</label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              [placeholder]="clientSecretPlaceholder"
              formControlName="clientSecret"
              [type]="clientSecretInputType"
              autocomplete="new-password"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              class="secret-visibility-button"
              [attr.aria-label]="clientSecretToggleLabel"
              [matTooltip]="clientSecretToggleLabel"
              (click)="toggleClientSecretVisibility()"
            >
              <mat-icon>{{ clientSecretVisible ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="payrollForm.get('clientSecret')?.hasError('required')">
              Client secret is required
            </mat-error>
          </mat-form-field>
          <p class="hint">
            Copy this from your UKG Ready system.
            <span *ngIf="canPreserveClientSecret">Leave it blank to keep the existing UKG Ready client secret.</span>
            If you don't know where to find it,
            <a href="https://campaign.wallit.app/ukg-wallit-connection-steps" target="_blank" rel="noopener noreferrer">see the instructions here</a>.
          </p>
        </div>
      </ng-container>

      <ng-container *ngIf="isPaycorProvider">
        <div class="field-row">
          <label>Environment</label>
          <mat-form-field appearance="outline" class="full-width">
            <mat-select formControlName="environment">
              <mat-option value="sandbox">Sandbox</mat-option>
            </mat-select>
            <mat-error *ngIf="payrollForm.get('environment')?.hasError('required')">
              Environment is required
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field-row">
          <label>Client ID</label>
          <mat-form-field appearance="outline" class="full-width">
            <input matInput placeholder="Enter Paycor client ID" formControlName="clientId" autocomplete="off" />
            <mat-error *ngIf="payrollForm.get('clientId')?.hasError('required')">
              Client ID is required
            </mat-error>
          </mat-form-field>
        </div>

        <div class="field-row">
          <label>Client Secret</label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              [placeholder]="clientSecretPlaceholder"
              formControlName="clientSecret"
              [type]="clientSecretInputType"
              autocomplete="new-password"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              class="secret-visibility-button"
              [attr.aria-label]="clientSecretToggleLabel"
              [matTooltip]="clientSecretToggleLabel"
              (click)="toggleClientSecretVisibility()"
            >
              <mat-icon>{{ clientSecretVisible ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="payrollForm.get('clientSecret')?.hasError('required')">
              Client secret is required
            </mat-error>
          </mat-form-field>
          <p class="hint" *ngIf="canPreservePaycorSecrets">Leave it blank to keep the existing Paycor client secret.</p>
        </div>

        <div class="field-row">
          <label>Subscription Key</label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              placeholder="Enter Paycor subscription key"
              formControlName="subscriptionKey"
              [type]="subscriptionKeyInputType"
              autocomplete="new-password"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              class="secret-visibility-button"
              [attr.aria-label]="subscriptionKeyToggleLabel"
              [matTooltip]="subscriptionKeyToggleLabel"
              (click)="toggleSubscriptionKeyVisibility()"
            >
              <mat-icon>{{ subscriptionKeyVisible ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="payrollForm.get('subscriptionKey')?.hasError('required')">
              Subscription key is required
            </mat-error>
          </mat-form-field>
          <p class="hint" *ngIf="canPreservePaycorSecrets">Leave it blank to keep the existing Paycor subscription key.</p>
        </div>

        <div class="field-row">
          <label>Refresh Token</label>
          <mat-form-field appearance="outline" class="full-width">
            <input
              matInput
              placeholder="Enter Paycor refresh token"
              formControlName="refreshToken"
              [type]="refreshTokenInputType"
              autocomplete="new-password"
            />
            <button
              mat-icon-button
              matSuffix
              type="button"
              class="secret-visibility-button"
              [attr.aria-label]="refreshTokenToggleLabel"
              [matTooltip]="refreshTokenToggleLabel"
              (click)="toggleRefreshTokenVisibility()"
            >
              <mat-icon>{{ refreshTokenVisible ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="payrollForm.get('refreshToken')?.hasError('required')">
              Refresh token is required
            </mat-error>
          </mat-form-field>
          <p class="hint" *ngIf="canPreservePaycorSecrets">Leave it blank to keep the existing Paycor refresh token.</p>
        </div>

        <div class="paycor-discovery-actions">
          <button
            mat-stroked-button
            type="button"
            color="primary"
            [disabled]="isDiscoveringPaycorLegalEntities"
            (click)="discoverPaycorLegalEntities()"
          >
            <mat-icon>{{ isDiscoveringPaycorLegalEntities ? 'hourglass_empty' : 'search' }}</mat-icon>
            {{ isDiscoveringPaycorLegalEntities ? 'Discovering' : 'Discover Legal Entities' }}
          </button>
          <p class="hint" *ngIf="paycorLegalEntityDiscoveryMessage">
            {{ paycorLegalEntityDiscoveryMessage }}
          </p>
        </div>

        <p class="paycor-discovery-error" *ngIf="paycorLegalEntityDiscoveryError">
          {{ paycorLegalEntityDiscoveryError }}
        </p>

        <div class="field-row" *ngIf="paycorLegalEntities.length > 0">
          <label>Activated Legal Entity</label>
          <mat-form-field appearance="outline" class="full-width">
            <mat-select
              [value]="payrollForm.get('legalEntityId')?.value"
              (selectionChange)="selectPaycorLegalEntity($event.value)"
            >
              <mat-option *ngFor="let entity of paycorLegalEntities" [value]="entity.legalEntityId">
                {{ entity.legalEntityName || 'Unnamed Legal Entity' }} - {{ entity.legalEntityId }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="optional-field-grid">
          <div class="field-row compact-field-row">
            <label>Legal Entity ID</label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput placeholder="Optional" formControlName="legalEntityId" autocomplete="off" />
            </mat-form-field>
          </div>

          <div class="field-row compact-field-row">
            <label>Legal Entity Name</label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput placeholder="Optional" formControlName="legalEntityName" autocomplete="off" />
            </mat-form-field>
          </div>
        </div>

        <p class="hint">
          Use discovery after entering Paycor credentials, or enter the legal entity ID manually.
        </p>
      </ng-container>
    </div>
  </ng-template>

  <ng-template [stepDialogStep]="'bank'">
    <div class="tab-card bank-card">
      <div class="secure-header">
        <mat-icon>verified_user</mat-icon>
        <div>
          <div class="secure-title">Secure Bank Connection</div>
          <div class="secure-subtitle">Powered by Stripe - your credentials are never stored</div>
        </div>
      </div>

      <div *ngIf="hasExistingBankConnection" class="edit-teams-only-note">
        A bank connection already exists for this company. Remove it from Connections to add a different bank account.
      </div>

      <div *ngIf="!hasExistingBankConnection" class="field-row">
        <label>Coverage</label>
        <p class="hint">
          This bank connection will automatically apply to all current and future teams in this company.
        </p>
        <p class="hint">
          Current teams: {{ companyTeamNamesLabel }}
        </p>
      </div>

      <div
        *ngIf="!hasExistingBankConnection"
        class="stripe-bank-element-wrap"
        [formGroup]="bankContactForm"
      >
        <div class="bank-contact-fields">
          <div class="field-row compact-field-row">
            <label>Full name</label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput placeholder="First and last name" formControlName="fullName" />
              <mat-error *ngIf="fullNameControl?.hasError('required')">
                Full name is required.
              </mat-error>
            </mat-form-field>
          </div>

          <div class="field-row compact-field-row">
            <label>Email</label>
            <mat-form-field appearance="outline" class="full-width">
              <input matInput placeholder="you@example.com" formControlName="email" />
              <mat-error *ngIf="emailControl?.hasError('required')">
                Email is required.
              </mat-error>
              <mat-error *ngIf="!emailControl?.hasError('required') && emailControl?.hasError('email')">
                Enter a valid email address.
              </mat-error>
            </mat-form-field>
          </div>
        </div>

        <p class="hint bank-contact-note">
          Stripe requires the full name and email associated with the bank account you are connecting.
          This may be different from the signed-in admin.
        </p>

        <div *ngIf="bankOwnerErrorMessage" class="bank-inline-error">
          {{ bankOwnerErrorMessage }}
        </div>

        <div #bankPaymentElementHost class="stripe-bank-element"></div>

        <div *ngIf="bankConnectionErrorMessage" class="bank-inline-error">
          {{ bankConnectionErrorMessage }}
        </div>
      </div>
    </div>
  </ng-template>
</app-step-dialog-shell>
